import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

const TIPO_TOKEN_PRIMEIRO_ACESSO = 1;

export default function PrimeiroAcesso() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validado, setValidado] = useState(false);
  const [status, setStatus] = useState(null); // valid | expired | used | not_found
  const [expiresAt, setExpiresAt] = useState(null);

  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const validar = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/auth/token/${TIPO_TOKEN_PRIMEIRO_ACESSO}/${token}`
        );

        const data = res.data?.data;
        setStatus(data?.status ?? "not_found");
        setExpiresAt(data?.expiresAt ?? null);
        setValidado(true);
      } catch (err) {
        console.error(err);
        setStatus("not_found");
        setValidado(true);
      } finally {
        setLoading(false);
      }
    };

    if (token) validar();
  }, [token]);

  const formatarDataBR = (dataIso) => {
    if (!dataIso) return "";

    const date = new Date(dataIso);

    return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).format(date);
    };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!senha || senha.length < 6) {
      AlertComponent("Erro", "A senha deve ter pelo menos 6 caracteres", { icon: "error" });
      return;
    }

    if (senha !== confirmarSenha) {
      AlertComponent("Erro", "As senhas não coincidem", { icon: "error" });
      return;
    }

    setSalvando(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/auth/primeiro-acesso/${token}`,
        { senha }
      );

      if (res.data?.success === false) {
        AlertComponent("Erro", res.data?.message ?? "Não foi possível definir a senha", { icon: "error" });
        return;
      }

      AlertComponent("Sucesso", "Senha definida com sucesso! Faça login para continuar.", { icon: "success" });
      navigate("/login");
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao definir senha", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <Loading />;

  // Se validou e não é válido
  if (validado && status !== "valid") {
    const mensagens = {
      expired: "Esse link expirou. Solicite um novo convite.",
      used: "Esse link já foi utilizado.",
      not_found: "Link inválido.",
      user_inactive: "Usuário inativo. Entre em contato com o suporte."
    };

    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-white p-8 rounded-xl shadow border">
          <h1 className="text-2xl font-semibold text-slate-800">Primeiro acesso</h1>
          <p className="text-sm text-slate-500 mt-2">
            {mensagens[status] ?? "Não foi possível validar o link."}
          </p>

          {expiresAt && (
            <p className="text-xs text-slate-400 mt-4">
              Expira em: {formatarDataBR(expiresAt)}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="px-5 py-2 rounded-md border hover:bg-gray-50"
            >
              Ir para login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Caso válido
  return (
    <div className="w-full max-w-xl mx-auto">
      <div className="bg-white p-8 rounded-xl shadow border">
        <h1 className="text-2xl font-semibold text-slate-800">Definir senha</h1>
        <p className="text-sm text-slate-500 mt-2">
          Crie uma senha para acessar o sistema.
        </p>

        {expiresAt && (
          <p className="text-xs text-slate-400 mt-4">
            Link expira em: {formatarDataBR(expiresAt)}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nova senha *
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="Mínimo 6 caracteres"
              required
              minLength={6}
              disabled={salvando}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar senha *
            </label>
            <input
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="Repita a senha"
              required
              minLength={6}
              disabled={salvando}
            />
          </div>

          <div className="flex justify-end gap-3 border-t pt-6">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50"
              disabled={salvando}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : "Definir senha"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
